import { Dialog } from 'quasar';
import type { FormMeta, GenericObject } from 'vee-validate';
import { type MaybeRef, unref } from 'vue';
import type { NavigationGuard, RouteLocationNormalizedGeneric } from 'vue-router';
import { i18n } from '@/plugins/i18n';
import type { Locales } from '@/services/types/api.types';

const t = i18n.global.t;

export interface DynamicTabsNavigationGuardCallbacks {
  onOk: () => void;
  onCancel: () => void;
}

export const dynamicTabsNavigationGuard = (
  meta: MaybeRef<FormMeta<GenericObject>>,
  callbackFns: DynamicTabsNavigationGuardCallbacks = { onOk: () => {}, onCancel: () => {} },
): void => {
  const isDirty = unref(meta).dirty;
  if (isDirty) {
    Dialog.create({
      title: t('global.dialogs.unsavedChanges.title'),
      message: t('global.dialogs.unsavedChanges.message'),
      persistent: true,
      ok: { label: t('global.dialogs.unsavedChanges.ok'), flat: true },
      cancel: { label: t('global.dialogs.unsavedChanges.cancel'), flat: true },
    })
      .onOk(callbackFns.onOk)
      .onCancel(callbackFns.onCancel);
  } else {
    callbackFns.onOk();
  }
};

export const formSaveNavigationGuard =
  (meta: MaybeRef<FormMeta<GenericObject>>): NavigationGuard =>
  (to, from, next) => {
    const isDirty = unref(meta).dirty;
    if (isDirty) {
      Dialog.create({
        title: t('global.dialogs.unsavedChanges.title'),
        message: t('global.dialogs.unsavedChanges.message'),
        persistent: true,
        ok: { label: t('global.dialogs.unsavedChanges.ok'), flat: true },
        cancel: { label: t('global.dialogs.unsavedChanges.cancel'), flat: true },
      })
        .onOk(() => {
          next();
        })
        .onCancel(() => {
          return false;
        });
    } else {
      next();
    }
  };

export const formSaveImageGuard = async (isDirty: boolean, callback: any) => {
  if (isDirty) {
    Dialog.create({
      title: t('global.dialogs.unsavedChanges.title'),
      message: t('global.dialogs.unsavedChanges.message'),
      persistent: true,
      ok: { label: t('global.dialogs.unsavedChanges.ok'), flat: true },
      cancel: { label: t('global.dialogs.unsavedChanges.cancel'), flat: true },
    })
      .onOk(async () => {
        callback();
      })
      .onCancel(() => {
        console.debug('Do nothing, admin will save update');
      });
  } else {
    callback();
  }
};

export const ensureLocalizableString = (object: any, key: string, languageEnabled: Locales[]) => {
  object[key] = object[key] || {};
  languageEnabled.forEach((lang) => {
    if (!(lang in object[key])) {
      object[key][lang] = null;
    }
  });
};

export const hasRouteChanged = (to: RouteLocationNormalizedGeneric, from: RouteLocationNormalizedGeneric): boolean => {
  return to.name !== from.name;
};

type ResettableForm = {
  resetForm: (state?: { values?: GenericObject }, opts?: { force?: boolean }) => void;
  errors: { value: Partial<Record<string, string>> };
  setErrors: (fields: Partial<Record<string, string | string[] | undefined>>) => void;
};

export const resetFormToItem = (form: ResettableForm, values: GenericObject): void => {
  form.resetForm({ values }, { force: true });
  const stalePaths = Object.keys(form.errors.value);
  if (stalePaths.length) {
    form.setErrors(Object.fromEntries(stalePaths.map((path) => [path, undefined])));
  }
};
